import { apiClient } from './client';
import {
  AcceptanceInspection,
  AcceptanceInspectionPayload,
  AcceptanceItemSuggestion,
} from '../types/acceptanceInspection';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchAcceptanceInspections(params?: {
  inventoryId?: number;
  contractId?: number;
  purchaseOrderId?: number;
  itemName?: string;
  specText?: string;
  keyword?: string;
}): Promise<AcceptanceInspection[]> {
  const { data } = await apiClient.get<AcceptanceInspection[]>('/acceptance-inspections', {
    params: {
      ...(params?.inventoryId != null ? { inventoryId: params.inventoryId } : {}),
      ...(params?.contractId != null ? { contractId: params.contractId } : {}),
      ...(params?.purchaseOrderId != null ? { purchaseOrderId: params.purchaseOrderId } : {}),
      ...(params?.itemName?.trim() ? { itemName: params.itemName.trim() } : {}),
      ...(params?.specText != null ? { specText: params.specText } : {}),
      ...(params?.keyword?.trim() ? { q: params.keyword.trim() } : {}),
    },
  });
  return data;
}

export async function fetchNextAcceptanceLotNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/acceptance-inspections/suggestions/lot-no'
  );
  return data;
}

export async function fetchAcceptanceItemSuggestions(
  keyword?: string
): Promise<AcceptanceItemSuggestion[]> {
  const { data } = await apiClient.get<AcceptanceItemSuggestion[]>(
    '/acceptance-inspections/item-suggestions',
    { params: { ...(keyword?.trim() ? { q: keyword.trim() } : {}) } }
  );
  return data;
}

export async function fetchAcceptanceInspection(
  inspectionId: number
): Promise<AcceptanceInspection> {
  const { data } = await apiClient.get<AcceptanceInspection>(
    `/acceptance-inspections/${inspectionId}`
  );
  return data;
}

export async function createAcceptanceInspection(
  payload: AcceptanceInspectionPayload
): Promise<AcceptanceInspection> {
  const { data } = await apiClient.post<AcceptanceInspection>('/acceptance-inspections', payload);
  return data;
}

export async function updateAcceptanceInspection(
  inspectionId: number,
  payload: Partial<AcceptanceInspectionPayload>
): Promise<AcceptanceInspection> {
  const { data } = await apiClient.patch<AcceptanceInspection>(
    `/acceptance-inspections/${inspectionId}`,
    payload
  );
  return data;
}

export async function deleteAcceptanceInspection(inspectionId: number): Promise<void> {
  await apiClient.delete(`/acceptance-inspections/${inspectionId}`);
}

export async function approveAcceptanceInspection(
  inspectionId: number
): Promise<AcceptanceInspection> {
  const { data } = await apiClient.post<AcceptanceInspection>(
    `/acceptance-inspections/${inspectionId}/approve`
  );
  return data;
}

export async function revokeAcceptanceInspection(
  inspectionId: number
): Promise<AcceptanceInspection> {
  const { data } = await apiClient.post<AcceptanceInspection>(
    `/acceptance-inspections/${inspectionId}/revoke`
  );
  return data;
}

export async function uploadAcceptanceInspectionAttachment(
  inspectionId: number,
  file: File,
  kind: 'MEDIA' | 'REMARK' = 'MEDIA'
): Promise<AcceptanceInspection> {
  const body = new FormData();
  body.append('file', file);
  const { data } = await apiClient.post<AcceptanceInspection>(
    `/acceptance-inspections/${inspectionId}/attachments`,
    body,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { kind },
    }
  );
  return data;
}

export async function deleteAcceptanceInspectionAttachment(
  inspectionId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/acceptance-inspections/${inspectionId}/attachments/${attachmentId}`);
}

export async function downloadAcceptanceInspectionAttachment(
  inspectionId: number,
  attachmentId: number
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/acceptance-inspections/${inspectionId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  return data;
}

export async function downloadAcceptanceInspectionPdf(
  inspectionId: number,
  fileName?: string
): Promise<void> {
  try {
    const { data } = await apiClient.get<Blob>(`/acceptance-inspections/${inspectionId}/pdf`, {
      responseType: 'blob',
    });
    if (data.type && data.type.includes('application/json')) {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string };
      throw new Error(parsed.message || 'PDF 다운로드에 실패했습니다.');
    }
    const url = window.URL.createObjectURL(data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName || `인수검사성적서_${inspectionId}.pdf`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const blob = (err as { response?: { data?: Blob } })?.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed.message) {
          throw new Error(parsed.message);
        }
      } catch (inner) {
        if (inner instanceof Error && inner.message && !inner.message.startsWith('Unexpected')) {
          throw inner;
        }
      }
    }
    if (err instanceof Error && err.message) {
      throw err;
    }
    throw err;
  }
}
